import {
  CustomerQuery,
  CustomerQuerySubscribeTime,
  CustomerQueryLocation,
  CustomerQueryLocationCond,
  CustomerQueryScore,
  CustomerQueryGroup,
  CustomerQueryRemark,
  CustomerQueryMemberCard,
  CustomerQueryProp,
  CustomerQueryPropItem,
  CustomerQueryPropUnary,
  CustomerQueryPropIn,
} from '../../models/CustomerQuery';
import customerFilter, {
  CustomerFilter,
  CustomFilter,
  ScoreFilter,
  FilterCompare,
} from '../../models/CustomerFilter';
import CustomerRemark from '@/customer/models/CustomerRemark';
import { RemarkGroup } from '@/customer/models/RemarkGroup';
import { PropType } from '@/customer/models/CustomerProp';
import moment from 'moment';

enum MultiMode {
  ContainAll = 'ContainAll',
  ContainAny = 'ContainAny',
  NotContainAll = 'NotContainAll',
  NotContainAny = 'NotContainAny',
}

export default {
  mkFilterQueries(f: CustomerFilter) {
    const filter = JSON.parse(JSON.stringify(f));
    const queries = {
      subscribe: true,
    } as CustomerQuery;
    if (filter.topics && filter.topics.length > 0) {
      queries.topics = filter.topics;
    } else {
      if (filter.sex !== -1) {
        queries.sex = filter.sex;
      }
      if (filter.topics && filter.topics.length > 0) {
        queries.topics = filter.topics;
      }
      if (filter.subscribe.periods[0]) {
        queries.subscribeTime = {
          notIn: filter.subscribe.notIn,
          start: filter.subscribe.periods[0] + ' 00:00:00',
          end: moment(filter.subscribe.periods[1]).add(1, 'days').format('YYYY-MM-DD') + ' 00:00:00',
        } as CustomerQuerySubscribeTime;
      }

      queries.subscribe = filter.subscribeStatus;

      if (filter.area.areas[0] &&
        filter.area.areas[0].length > 0 &&
        filter.area.areas[0] !== '') {
        queries.location = {
          notIn: filter.area.notIn,
          conds: this.getCustomerQueryLocationCond(filter),
        } as CustomerQueryLocation;
      }
      if (filter.subscribeScene.scenes.length > 0) {
        queries.subscribeScene = {
          notIn: filter.subscribeScene.notIn,
          scenes: filter.subscribeScene.scenes,
        };
      }
      if (filter.interact.period > -1) {
        if (filter.interact.period === 0) {
          queries.interactive = { range: { Today: {} } };
        } else {
          queries.interactive = { range: { Days: { days: filter.interact.period } } };
        }
      }
      if (filter.topicQuery && filter.topicQuery.topics.length > 0) {
        queries.topicQuery = {
          topics: filter.topicQuery.topics,
          notIn: filter.topicQuery.notIn,
        };
      }
      if (filter.interact.interactiveType &&
        filter.interact.interactiveType.length > 0) {
        queries.interactive!.interactiveType = filter.interact.interactiveType;
      }
      if (filter.trace.compare > 0) {
        if (filter.trace.count > 0) {
          queries.trace = {};
          queries.trace.min = filter.trace.count;
        }
      } else {
        queries.trace = {};
        queries.trace.max = filter.trace.count;
      }
      if (filter.scoreSum) {
        if (filter.scoreSum.minScore || filter.scoreSum.maxScore) {
          queries.scoreSum = {} as CustomerQueryScore;
          queries.scoreSum.min = filter.scoreSum.minScore || 0;
          queries.scoreSum.max = filter.scoreSum.maxScore || 2147483647;
        }
      } else {
        if (filter.score.minScore || filter.score.maxScore) {
          queries.score = {} as CustomerQueryScore;
          queries.score.min = filter.score.minScore || 0;
          queries.score.max = filter.score.maxScore || 2147483647;
        }
      }
      if (filter.member && filter.member.memberIds.length > 0) {
        queries.memberCard = {
          notIn: filter.member.notIn,
          cardId: filter.member.memberIds,
        } as CustomerQueryMemberCard;
      }
      if (filter.group.groupIds.length > 0) {
        queries.group = {
          mode: this.makeMode(filter.group.notIn, filter.group.or),
          ids: filter.group.groupIds,
        } as CustomerQueryGroup;
      }
      if (filter.remark && (filter.remark.remarks.length > 0 || (filter.remark.remarkGroups && filter.remark.remarkGroups.length > 0))) {
        queries.remark = {
          mode: this.makeMode(filter.remark.notIn, filter.remark.or),
          ids: filter.remark.remarks.map((rmk: CustomerRemark) => rmk.id),
          groupIds: filter.remark.remarkGroups && filter.remark.remarkGroups.map((g: RemarkGroup) => g.id),
        } as CustomerQueryRemark;
      }
      if (filter.custom && filter.custom.length > 0) {
        queries.prop = {
          conds: this.validCustoms(filter).map((c: CustomFilter) => {
            let item: CustomerQueryPropItem<any> = {} as CustomerQueryPropItem<any>;
            const fieldName = c.prop!.builtIn ? c.prop!.fieldName : `privateProps.${c.prop!.fieldName}`;
            let op;
            if (c.date) {
              switch (c.greater) {
                case 0:
                  op = 'Eq';
                  break;
                case 1:
                  op = 'Gt';
                  break;
                case -1:
                  op = 'Lt';
                  break;
              }
              item = {
                [op]: {
                  field: fieldName,
                  value: c.date!,
                } as CustomerQueryPropUnary<string>,
              } as CustomerQueryPropItem<string>;
            } else if (c.num) {
              if (c.greater === FilterCompare.Greater) {
                item = {
                  Gt: {
                    field: fieldName,
                    value: c.num!,
                  } as CustomerQueryPropUnary<number>,
                } as CustomerQueryPropItem<number>;
              } else if (c.greater === FilterCompare.Less) {
                item = {
                  Lt: {
                    field: fieldName,
                    value: c.num!,
                  } as CustomerQueryPropUnary<number>,
                } as CustomerQueryPropItem<number>;
              } else {
                item = {
                  Eq: {
                    field: fieldName,
                    value: c.num!,
                  } as CustomerQueryPropUnary<number>,
                } as CustomerQueryPropItem<number>;
              }
            } else if (c.nums) {
              item = {
                In: {
                  field: fieldName,
                  values: c.nums!,
                  notIn: c.notIn,
                } as CustomerQueryPropIn<number>,
              } as CustomerQueryPropItem<number>;
            } else {
              item = {
                In: {
                  field: fieldName,
                  values: c.keywords!,
                  notIn: c.notIn,
                } as CustomerQueryPropIn<string>,
              } as CustomerQueryPropItem<string>;
            }
            return item;
          }),
        } as CustomerQueryProp;
      }
    }
    return queries;
  },
  getCustomerQueryLocationCond(filter: CustomerFilter): CustomerQueryLocationCond[] {
    return filter.area.areas.map((r) => {
      const cond = {} as CustomerQueryLocationCond;
      if (r[0] === '海外') {
        cond.country = r[1] === '所有' ? '海外' : r[1];
      } else {
        cond.country = r[0];
        if (r[1] && r[1] !== '所有') { cond.province = r[1]; }
        if (r[2] && r[2] !== '所有') { cond.city = r[2]; }
      }
      return cond;
    });
  },
  makeMode(notIn: boolean, or: boolean): string {
    if (notIn) {
      return or ? MultiMode.NotContainAny : MultiMode.NotContainAll;
    } else {
      return or ? MultiMode.ContainAny : MultiMode.ContainAll;
    }
  },
  extractMode(mode: MultiMode): [boolean, boolean] {
    if (mode === MultiMode.NotContainAny) {
      return [true, true];
    } else if (mode === MultiMode.NotContainAll) {
      return [true, false];
    } else if (mode === MultiMode.ContainAll) {
      return [false, false];
    } else {
      return [false, true];
    }
  },
  query2Filter(q: CustomerQuery, remarks: CustomerRemark[] = [], remarkGroups: RemarkGroup[] = []): CustomerFilter {
    const queries = JSON.parse(JSON.stringify(q));
    const filter = JSON.parse(JSON.stringify(customerFilter));
    if (queries.topics && queries.topics.length > 0) {
      filter.topics = queries.topics;
    } else {
      if (queries.sex !== undefined &&
        queries.sex !== null &&
        queries.sex !== -1) {
        filter.sex = queries.sex;
      } else {
        filter.sex = -1;
      }
      if (queries.topicQuery && queries.topicQuery.topics.length > 0) {
        filter.topicQuery.topics = queries.topicQuery.topics;
        filter.topicQuery.notIn = queries.topicQuery.notIn;
      }
      if (queries.subscribeTime) {
        filter.area.notIn = queries.subscribeTime.notIn;
        filter.subscribe.periods = [
          queries.subscribeTime.start.substring(0, 10),
          moment(queries.subscribeTime.end).add(-1, 'days').format('YYYY-MM-DD'),
        ];
      }
      if (queries.subscribeScene) {
        filter.subscribeScene = {
          notIn: queries.subscribeScene.notIn,
          scenes: queries.subscribeScene.scenes,
        };
      }
      if (queries.location) {
        filter.area.notIn = queries.location.notIn;
        filter.area.areas = queries.location.conds.map((r: any) => {
          if (r.country !== '中国') {
            return ['海外', r.country];
          } else {
            const loc = [];
            loc.push(r.country);
            if (r.province) {
              loc.push(r.province);
            }
            if (r.city) {
              loc.push(r.city);
            }
            return loc;
          }
        });
      }
      if (queries.memberCard) {
        filter.member = {
          notIn: queries.memberCard.notIn,
          memberIds: queries.memberCard.cardId,
        };
      }
      if (queries.interactive) {
        if (queries.interactive.range && queries.interactive.range.Days) {
          filter.interact.period = queries.interactive.range.Days.days;
        } else {
          filter.interact.period = 0;
        }
        if (queries.interactive.interactiveType &&
          queries.interactive!.interactiveType.length > 0) {
          filter.interact.interactiveType = queries.interactive.interactiveType;
        }
      } else {
        filter.interact.period = -1;
      }
      if (queries.trace) {
        if (queries.trace.min) {
          filter.trace.compare = 1;
          filter.trace.count = queries.trace.min;
        }
        if (queries.trace.max) {
          filter.trace.compare = -1;
          filter.trace.count = queries.trace.max;
        }
      }
      if (queries.score) {
        filter.score = {} as ScoreFilter;
        if (queries.score.min > 0) {
          filter.score!.minScore = queries.score.min;
        }
        if (queries.score.max < 2147483647) {
          filter.score!.maxScore = queries.score.max;
        }
      }
      if (queries.scoreSum) {
        filter.scoreSum = {} as ScoreFilter;
        if (queries.scoreSum.min > 0) {
          filter.scoreSum!.minScore = queries.scoreSum.min;
        }
        if (queries.scoreSum.max < 2147483647) {
          filter.scoreSum!.maxScore = queries.scoreSum.max;
        }
      }
      if (queries.group) {
        const [notIn, or] = this.extractMode(queries.group.mode);
        filter.group.notIn = notIn;
        filter.group.or = or;
        filter.group.groupIds = queries.group.ids;
      }
      if (queries.remark && (queries.remark.ids.length > 0 || queries.remark.groupIds.length > 0)) {
        const [notIn, or] = this.extractMode(queries.remark.mode);
        filter.remark.notIn = notIn;
        filter.remark.or = or;
        filter.remark.remarks = queries.remark.ids.map((id: number) => {
          const r = remarks.find((r: CustomerRemark) => r.id === id);
          return r!;
        });
        filter.remark.remarkGroups = queries.remark.groupIds && queries.remark.groupIds.map((i: number) => remarkGroups.find((r) => r.id === i));
      }
    }
    return filter;
  },
  validCustoms(filter: CustomerFilter): CustomFilter[] {
    return filter.custom.filter((custom: CustomFilter) => {
      if (custom.prop === undefined) {
        return false;
      } else {
        if ([PropType.LocalDate, PropType.DateTime].includes(custom.prop.propType)) {
          return custom.date;
        } else if (PropType.Numeric === custom.prop.propType) {
          return custom.num !== undefined || (custom.nums && custom.nums.length);
        } else {
          return custom.keywords !== undefined && custom.keywords.length;
        }
      }
    });
  },
};
